import { DocumentAdminLink } from '../legal/DocumentAdminAccess';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  FiChevronLeft,
  FiMail,
  FiMessageCircle,
  FiPaperclip,
  FiFilePlus,
  FiX,
  FiCheckCircle,
} from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { SITE_URL } from '../../utils/env';
import api from '../../api/api';
import { contactApi } from '../../api/contact';
import { AccountBoundary, useSettings, useRequestScope, unwrap } from '../settings/shared';
import ContactDraftGuard from './ContactDraftGuard';
import { useExpandedText } from '../notifications/useExpandedText';
import { PHONE_PREFIXES, albumCorrectionMessage, fileSizeLabel } from './nativeModel';
import { translate, validateContact, phoneMiddle, addFiles, contactPayload } from './model';
import './Contact.css';
export default function ContactForm() {
  return <AccountBoundary component={Form} />;
}
function Form({ user, validateSession }) {
  const { locale, style, en } = useSettings(),
    { state } = useLocation(),
    nav = useNavigate(),
    request = useRequestScope(user),
    expanded = useExpandedText();
  const t = (k, v) => translate(locale, k, v),
    correction = state?.kind === 'albumCorrection';
  const [initial] = useState(() => albumCorrectionMessage(state, t));
  const blank = () => ({
    name: '',
    email: '',
    prefix: '010',
    middle: '',
    last: '',
    message: initial,
  });
  const [form, setForm] = useState(blank),
    [files, setFiles] = useState([]),
    [errors, setErrors] = useState({}),
    [busy, setBusy] = useState(false),
    [complete, setComplete] = useState(false);
  const lock = useRef(false),
    guard = useRef({ released: false }),
    fileInput = useRef(),
    middleInput = useRef(),
    emailInput = useRef(),
    lastInput = useRef(),
    messageInput = useRef();
  guard.current = {
    released: complete,
    dirty:
      !complete &&
      (Boolean(form.name || form.email || form.middle || form.last || files.length) ||
        form.prefix !== '010' ||
        form.message !== initial),
    busy,
  };
  const doc = useQuery({
    queryKey: ['document', 'CONTACT', locale],
    enabled: !correction,
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await api.getPublic(`/about?type=CONTACT&locale=${locale}`, {
          signal,
          suppressGlobalError: true,
        }),
      ),
  });
  const html =
    doc.data?.content &&
    (locale === 'ko' || (doc.data.locale === 'en' && doc.data.fallback !== true))
      ? DOMPurify.sanitize(doc.data.content, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li'],
          ALLOWED_ATTR: ['href', 'title'],
        })
      : '';
  const edit = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: '', submit: '' }));
  };
  const back = () => (window.history.state?.idx > 0 ? nav(-1) : nav('/mypage/settings'));
  useEffect(() => {
    const resize = () => {
      if (messageInput.current) {
        messageInput.current.style.height = 'auto';
        messageInput.current.style.height = Math.max(166, messageInput.current.scrollHeight) + 'px';
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [form.message, expanded]);

  const submit = async (event) => {
    event.preventDefault();
    if (lock.current || !validateSession()) return;
    const invalid = validateContact(form, t);
    setErrors(invalid);
    if (Object.keys(invalid).length) return;
    lock.current = true;
    setBusy(true);
    const r = request();
    try {
      await contactApi.sendEmail(contactPayload(form), files, r.options);
      if (r.current()) {
        setComplete(true);
        setFiles([]);
        setForm(blank());
        window.scrollTo(0, 0);
      }
    } catch {
      if (r.current()) setErrors({ submit: t('contact.submitError') });
    } finally {
      lock.current = false;
      if (r.mounted()) {
        setBusy(false);
        if (!r.current()) setErrors({ submit: t('common.retryLater') });
      }
      r.finish();
    }
  };
  const selectFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selected.length) return;
    const next = addFiles(files, selected, t);
    setFiles(next.files);
    setErrors((e) => ({ ...e, attachments: next.error }));
  };
  const title = t(correction ? 'contact.correctionTitle' : 'contact.title');
  const field = (key, label, placeholder, type = 'text') => (
    <div className="pp-contact-field">
      <label htmlFor={'contact-' + key}>
        {t(label)} <span aria-hidden="true">*</span>
      </label>
      <div className={'pp-contact-input-wrap' + (key === 'email' ? ' has-clear' : '')}>
        <input
          ref={key === 'email' ? emailInput : undefined}
          id={'contact-' + key}
          name={key}
          type={type}
          value={form[key]}
          autoComplete={key === 'name' ? 'name' : 'email'}
          placeholder={t(placeholder)}
          required
          aria-invalid={!!errors[key]}
          aria-describedby={errors[key] ? 'contact-error-' + key : undefined}
          onChange={(e) => edit(key, e.target.value)}
        />
        {key === 'email' && form.email && (
          <button
            className="pp-contact-clear"
            type="button"
            aria-label={en ? 'Clear email' : '이메일 지우기'}
            onClick={() => {
              edit('email', '');
              emailInput.current?.focus();
            }}
          >
            <FiX size={18} />
          </button>
        )}
      </div>
      {errors[key] && (
        <small id={'contact-error-' + key} role="alert">
          {errors[key]}
        </small>
      )}
    </div>
  );
  return (
    <PageLayout paddingTop={0}>
      <div className={'pp-settings pp-contact' + (expanded ? ' is-expanded' : '')} style={style}>
        <SEO title={title} description={t('contact.generalIntro')} url={`${SITE_URL}/contact`} />
        <ContactDraftGuard state={guard} t={t} />
        <header className="pp-settings-header">
          <button type="button" aria-label={t('common.back')} disabled={busy} onClick={back}>
            <FiChevronLeft size={22} />
          </button>
          <h1>{title}</h1>
        </header>
        {!correction && <DocumentAdminLink disabled={busy} />}
        {complete ? (
          <section className="pp-contact-success" role="status">
            <span className="pp-contact-success-icon">
              <FiCheckCircle size={34} />
            </span>
            <div>
              <h2>{t(correction ? 'contact.correctionSent' : 'contact.sent')}</h2>
              <p>{t('contact.sentBody')}</p>
            </div>
            <div className="pp-contact-success-actions">
              {!correction && (
                <button
                  className="pp-contact-submit secondary"
                  onClick={() => {
                    setForm(blank());
                    setFiles([]);
                    setErrors({});
                    setComplete(false);
                  }}
                >
                  {t('contact.writeAnother')}
                </button>
              )}
              <button className="pp-contact-submit" onClick={back}>
                {t('contact.back')}
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="pp-contact-intro">
              <span className="pp-contact-icon">
                {correction ? <FiMessageCircle size={21} /> : <FiMail size={21} />}
              </span>
              {!correction && html ? (
                <div className="pp-contact-copy" dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <p>{t(correction ? 'contact.correctionIntro' : 'contact.generalIntro')}</p>
              )}
            </section>
            <form onSubmit={submit} noValidate>
              <fieldset disabled={busy} className="pp-contact-fields">
                {field('name', 'contact.name', 'contact.namePlaceholder')}
                {field('email', 'contact.email', 'contact.emailPlaceholder', 'email')}
                <div className="pp-contact-field">
                  <label htmlFor="contact-prefix">{t('contact.phone')}</label>
                  <div className="pp-contact-phone">
                    <select
                      id="contact-prefix"
                      aria-label={t('contact.phonePrefixA11y', { prefix: form.prefix })}
                      value={form.prefix}
                      onChange={(e) => {
                        edit('prefix', e.target.value);
                        middleInput.current?.focus();
                        setErrors((x) => ({ ...x, telephone: '' }));
                      }}
                    >
                      {PHONE_PREFIXES.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                    <span aria-hidden="true">–</span>
                    <input
                      ref={middleInput}
                      aria-label={t('contact.phoneMiddleA11y')}
                      inputMode="numeric"
                      autoComplete="tel-local-prefix"
                      placeholder="0000"
                      value={form.middle}
                      aria-invalid={!!errors.telephone}
                      onChange={(e) => {
                        const next = phoneMiddle(e.target.value, form.prefix);
                        setForm((f) => ({
                          ...f,
                          middle: next.middle,
                          ...(next.last === undefined ? {} : { last: next.last }),
                        }));
                        setErrors((x) => ({ ...x, telephone: '', submit: '' }));
                        if (next.focus) lastInput.current?.focus();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          lastInput.current?.focus();
                        }
                      }}
                    />
                    <span aria-hidden="true">–</span>
                    <input
                      ref={lastInput}
                      aria-label={t('contact.phoneLastA11y')}
                      inputMode="numeric"
                      autoComplete="tel-local-suffix"
                      maxLength={4}
                      placeholder="0000"
                      value={form.last}
                      aria-invalid={!!errors.telephone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        edit('last', value);
                        setErrors((x) => ({ ...x, telephone: '' }));
                        if (value.length === 4) messageInput.current?.focus();
                      }}
                    />
                  </div>
                  {errors.telephone && <small role="alert">{errors.telephone}</small>}
                </div>
                <div className="pp-contact-field">
                  <label htmlFor="contact-message">
                    {t('contact.message')} <span aria-hidden="true">*</span>
                  </label>
                  <textarea
                    ref={messageInput}
                    id="contact-message"
                    required
                    maxLength={5000}
                    value={form.message}
                    placeholder={t('contact.messagePlaceholder')}
                    aria-invalid={!!errors.message}
                    aria-describedby="contact-counter"
                    onChange={(e) => edit('message', e.target.value)}
                  />
                  {errors.message && <small role="alert">{errors.message}</small>}
                  <div className="pp-contact-count" id="contact-counter">
                    {form.message.length.toLocaleString(en ? 'en-US' : 'ko-KR')} / 5,000
                  </div>
                </div>
                <div className="pp-contact-field">
                  <div className="pp-contact-attachment-heading">
                    <strong>{t('contact.attachmentTitle')}</strong>
                    <span>{t('contact.attachmentMeta')}</span>
                  </div>
                  {files.map((file, i) => (
                    <div className="pp-contact-attachment" key={i}>
                      <FiPaperclip size={17} />
                      <div>
                        <strong>{file.name}</strong>
                        <span>{fileSizeLabel(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        aria-label={t('contact.removeAttachment', { name: file.name })}
                        onClick={() => setFiles((f) => f.filter((_, idx) => i !== idx))}
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ))}
                  <input
                    ref={fileInput}
                    className="pp-contact-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif"
                    multiple
                    aria-label={t('contact.chooseAttachment')}
                    onChange={selectFiles}
                  />
                  <button
                    type="button"
                    className="pp-contact-choose"
                    onClick={() => {
                      if (files.length >= 3)
                        setErrors((x) => ({
                          ...x,
                          attachments: t('contact.attachmentLimit', { count: 3 }),
                        }));
                      else fileInput.current?.click();
                    }}
                  >
                    <FiFilePlus size={18} />
                    {t('contact.chooseFile')}
                  </button>
                  {errors.attachments && <small role="alert">{errors.attachments}</small>}
                </div>
                {errors.submit && (
                  <p role="alert" className="pp-contact-error">
                    {errors.submit}
                  </p>
                )}
                <button className="pp-contact-submit" type="submit" aria-busy={busy}>
                  {busy ? t('common.loading') : t('contact.submit')}
                </button>
              </fieldset>
            </form>
          </>
        )}
      </div>
    </PageLayout>
  );
}
